import { useTheme } from '../../contexts/ThemeContext';
import { SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';

interface ThemeToggleProps {
  className?: string;
  variant?: 'icon' | 'button' | 'dropdown';
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
};

const iconSizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

export function ThemeToggle({
  className,
  variant = 'icon',
  size = 'md',
}: ThemeToggleProps) {
  const { theme, resolvedTheme, setTheme, toggleTheme } = useTheme();

  if (variant === 'dropdown') {
    return (
      <Menu as="div" className={cn('relative', className)}>
        <Menu.Button
          className={cn(
            'flex items-center justify-center rounded-lg',
            'text-gray-600 dark:text-gray-400',
            'hover:bg-gray-100 dark:hover:bg-dark-800',
            'transition-colors duration-200',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            sizeClasses[size]
          )}
          aria-label="Toggle theme"
        >
          {resolvedTheme === 'dark' ? (
            <MoonIcon className={iconSizeClasses[size]} />
          ) : (
            <SunIcon className={iconSizeClasses[size]} />
          )}
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            className={cn(
              'absolute right-0 mt-2 w-40 origin-top-right',
              'rounded-xl bg-white dark:bg-dark-800',
              'border border-gray-200 dark:border-dark-700',
              'shadow-lg ring-1 ring-black ring-opacity-5',
              'focus:outline-none',
              'py-1'
            )}
          >
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => setTheme('light')}
                  className={cn(
                    'flex items-center gap-3 w-full px-4 py-2 text-sm',
                    active
                      ? 'bg-gray-100 dark:bg-dark-700'
                      : 'text-gray-700 dark:text-gray-300',
                    theme === 'light' && 'text-primary-600 dark:text-primary-400'
                  )}
                >
                  <SunIcon className="h-4 w-4" />
                  Light
                  {theme === 'light' && (
                    <span className="ml-auto text-primary-500">✓</span>
                  )}
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => setTheme('dark')}
                  className={cn(
                    'flex items-center gap-3 w-full px-4 py-2 text-sm',
                    active
                      ? 'bg-gray-100 dark:bg-dark-700'
                      : 'text-gray-700 dark:text-gray-300',
                    theme === 'dark' && 'text-primary-600 dark:text-primary-400'
                  )}
                >
                  <MoonIcon className="h-4 w-4" />
                  Dark
                  {theme === 'dark' && (
                    <span className="ml-auto text-primary-500">✓</span>
                  )}
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => setTheme('system')}
                  className={cn(
                    'flex items-center gap-3 w-full px-4 py-2 text-sm',
                    active
                      ? 'bg-gray-100 dark:bg-dark-700'
                      : 'text-gray-700 dark:text-gray-300',
                    theme === 'system' && 'text-primary-600 dark:text-primary-400'
                  )}
                >
                  <ComputerDesktopIcon className="h-4 w-4" />
                  System
                  {theme === 'system' && (
                    <span className="ml-auto text-primary-500">✓</span>
                  )}
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>
    );
  }

  if (variant === 'button') {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg',
          'text-gray-700 dark:text-gray-300',
          'bg-gray-100 dark:bg-dark-800',
          'hover:bg-gray-200 dark:hover:bg-dark-700',
          'transition-colors duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          className
        )}
        aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
      >
        {resolvedTheme === 'dark' ? (
          <>
            <SunIcon className={iconSizeClasses[size]} />
            <span className="text-sm font-medium">Light Mode</span>
          </>
        ) : (
          <>
            <MoonIcon className={iconSizeClasses[size]} />
            <span className="text-sm font-medium">Dark Mode</span>
          </>
        )}
      </button>
    );
  }

  // Icon variant (default)
  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={cn(
        'relative flex items-center justify-center rounded-lg',
        'text-gray-600 dark:text-gray-400',
        'hover:bg-gray-100 dark:hover:bg-dark-800',
        'hover:text-gray-900 dark:hover:text-white',
        'transition-all duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        sizeClasses[size],
        className
      )}
      aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
    >
      <span className="sr-only">Toggle theme</span>

      {/* Sun icon */}
      <SunIcon
        className={cn(
          iconSizeClasses[size],
          'absolute transition-all duration-300',
          resolvedTheme === 'dark'
            ? 'opacity-100 rotate-0 scale-100'
            : 'opacity-0 rotate-90 scale-0'
        )}
      />

      {/* Moon icon */}
      <MoonIcon
        className={cn(
          iconSizeClasses[size],
          'absolute transition-all duration-300',
          resolvedTheme === 'light'
            ? 'opacity-100 rotate-0 scale-100'
            : 'opacity-0 -rotate-90 scale-0'
        )}
      />
    </button>
  );
}
